import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const UserPreferences = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [language, setLanguage] = useState('en');
  const [timezone, setTimezone] = useState('UTC');
  const [dateFormat, setDateFormat] = useState('MM/DD/YYYY');
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    sms: false,
    agentUpdates: true,
    systemAlerts: true,
    weeklyReports: false,
    marketingEmails: false
  });

  const languageOptions = [
    { value: 'en', label: 'English (US)' },
    { value: 'en-gb', label: 'English (UK)' },
    { value: 'es', label: 'Español' },
    { value: 'fr', label: 'Français' },
    { value: 'de', label: 'Deutsch' },
    { value: 'ja', label: '日本語' },
    { value: 'zh', label: '中文' }
  ];

  const timezoneOptions = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
    { value: 'Europe/Paris', label: 'Central European Time (CET)' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' }
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US Format)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (UK Format)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO Format)' },
    { value: 'DD MMM YYYY', label: 'DD MMM YYYY (e.g., 29 Oct 2024)' }
  ];

  useEffect(() => {
    // Load saved preferences from localStorage
    const savedTheme = localStorage.getItem('theme');
    const savedLanguage = localStorage.getItem('language');
    const savedTimezone = localStorage.getItem('timezone');
    const savedDateFormat = localStorage.getItem('dateFormat');
    const savedNotifications = localStorage.getItem('notifications');

    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark');
    }
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }
    if (savedTimezone) {
      setTimezone(savedTimezone);
    }
    if (savedDateFormat) {
      setDateFormat(savedDateFormat);
    }
    if (savedNotifications) {
      setNotifications(JSON.parse(savedNotifications));
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    document.documentElement?.classList?.toggle('dark', newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  const handleTimezoneChange = (newTimezone) => {
    setTimezone(newTimezone);
    localStorage.setItem('timezone', newTimezone);
  };

  const handleDateFormatChange = (newFormat) => {
    setDateFormat(newFormat);
    localStorage.setItem('dateFormat', newFormat);
  };

  const handleNotificationChange = (key, value) => {
    const updatedNotifications = { ...notifications, [key]: value };
    setNotifications(updatedNotifications);
    localStorage.setItem('notifications', JSON.stringify(updatedNotifications));
  };

  const resetToDefaults = () => {
    setIsDarkMode(false);
    setLanguage('en');
    setTimezone('UTC');
    setDateFormat('MM/DD/YYYY');
    setNotifications({
      email: true,
      push: false,
      sms: false,
      agentUpdates: true,
      systemAlerts: true,
      weeklyReports: false,
      marketingEmails: false
    });
    
    // Clear localStorage
    localStorage.removeItem('theme');
    localStorage.removeItem('language');
    localStorage.removeItem('timezone');
    localStorage.removeItem('dateFormat');
    localStorage.removeItem('notifications');
    
    document.documentElement?.classList?.remove('dark');
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">User Preferences</h3>
          <p className="text-sm text-text-secondary mt-1">
            Customize your experience and notification settings
          </p>
        </div>
        <Button variant="outline" onClick={resetToDefaults}>
          Reset to Defaults
        </Button>
      </div>
      {/* Theme Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-semibold text-card-foreground mb-4 flex items-center gap-2">
          <Icon name="Palette" size={20} className="text-primary" />
          Appearance
        </h4>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h5 className="font-medium text-card-foreground">Dark Mode</h5>
              <p className="text-sm text-text-secondary">
                Switch between light and dark theme
              </p>
            </div>
            <Button
              variant={isDarkMode ? "default" : "outline"}
              size="sm"
              onClick={toggleTheme}
              iconName={isDarkMode ? "Moon" : "Sun"}
              iconPosition="left"
            >
              {isDarkMode ? 'Dark' : 'Light'}
            </Button>
          </div>
        </div>
      </div>
      {/* Localization Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-semibold text-card-foreground mb-4 flex items-center gap-2">
          <Icon name="Globe" size={20} className="text-primary" />
          Localization
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Select
            label="Language"
            description="Choose your preferred language"
            options={languageOptions}
            value={language}
            onChange={handleLanguageChange}
          />
          
          <Select
            label="Timezone"
            description="Set your local timezone"
            options={timezoneOptions}
            value={timezone}
            onChange={handleTimezoneChange}
            searchable
          />
          
          <Select
            label="Date Format"
            description="Choose date display format"
            options={dateFormatOptions}
            value={dateFormat}
            onChange={handleDateFormatChange}
          />
        </div>
      </div>
      {/* Notification Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-semibold text-card-foreground mb-4 flex items-center gap-2">
          <Icon name="Bell" size={20} className="text-primary" />
          Notifications
        </h4>
        
        <div className="space-y-6">
          <div>
            <h5 className="font-medium text-card-foreground mb-3">Delivery Methods</h5>
            <div className="space-y-3">
              <Checkbox
                label="Email Notifications"
                description="Receive notifications via email"
                checked={notifications?.email}
                onChange={(e) => handleNotificationChange('email', e?.target?.checked)}
              />
              <Checkbox
                label="Push Notifications"
                description="Browser push notifications"
                checked={notifications?.push}
                onChange={(e) => handleNotificationChange('push', e?.target?.checked)}
              />
              <Checkbox
                label="SMS Notifications"
                description="Text message notifications for critical alerts"
                checked={notifications?.sms}
                onChange={(e) => handleNotificationChange('sms', e?.target?.checked)}
              />
            </div>
          </div>
          
          <div>
            <h5 className="font-medium text-card-foreground mb-3">Notification Types</h5>
            <div className="space-y-3">
              <Checkbox
                label="Agent Updates"
                description="Notifications about agent launches and completions"
                checked={notifications?.agentUpdates}
                onChange={(e) => handleNotificationChange('agentUpdates', e?.target?.checked)}
              />
              <Checkbox
                label="System Alerts"
                description="Important system maintenance and security alerts"
                checked={notifications?.systemAlerts}
                onChange={(e) => handleNotificationChange('systemAlerts', e?.target?.checked)}
              />
              <Checkbox
                label="Weekly Reports"
                description="Weekly summary of your account activity"
                checked={notifications?.weeklyReports}
                onChange={(e) => handleNotificationChange('weeklyReports', e?.target?.checked)}
              />
              <Checkbox
                label="Marketing Emails"
                description="Product updates and promotional content"
                checked={notifications?.marketingEmails}
                onChange={(e) => handleNotificationChange('marketingEmails', e?.target?.checked)}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Account Security */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-semibold text-card-foreground mb-4 flex items-center gap-2">
          <Icon name="Shield" size={20} className="text-primary" />
          Security & Privacy
        </h4>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h5 className="font-medium text-card-foreground">Two-Factor Authentication</h5>
              <p className="text-sm text-text-secondary">
                Add an extra layer of security to your account
              </p>
            </div>
            <Button variant="outline" size="sm">
              Configure
            </Button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h5 className="font-medium text-card-foreground">Active Sessions</h5>
              <p className="text-sm text-text-secondary">
                Manage your active login sessions
              </p>
            </div>
            <Button variant="outline" size="sm">
              View Sessions
            </Button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h5 className="font-medium text-card-foreground">Data Export</h5>
              <p className="text-sm text-text-secondary">
                Download a copy of your account data
              </p>
            </div>
            <Button variant="outline" size="sm">
              Request Export
            </Button>
          </div>
        </div>
      </div>
      {/* Save Changes */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
        <Button variant="outline">
          Cancel Changes
        </Button>
        <Button variant="default">
          Save Preferences
        </Button>
      </div>
    </div>
  );
};

export default UserPreferences;